import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { GearSix, ShieldCheck, BellSlash, ArrowsClockwise, Compass, Warning, ChatCircleDots, Path } from "@phosphor-icons/react";
import api, { apiErr } from "../api";
import { brandIconSquare } from "../brand";
import InstallApp from "../components/InstallApp";
import AudioSettingsPanel from "../components/AudioSettingsPanel";
import { useBeta } from "../context/BetaContext";

const PRIVACY_TOGGLES = [
  ["leaderboard_opt_out", "Hide me from public leaderboards"],
  ["anonymous_name", "Show me under a stable anonymous alias"],
  ["disable_rival_follows", "Don't let anyone follow me as a rival"],
  ["hide_from_rivals", "Hide me from rival discovery"],
  ["hide_favorite_crew", "Hide my favorite crew member"],
  ["hide_achievement_activity", "Hide my public achievement activity"],
  ["disable_profile_linking", "Disable public-profile linking"],
  ["disable_rival_notifications", "Disable all rival notifications"],
  ["hide_competitive_activity", "Hide my recent competitive activity"],
];

const NOTIF_TOGGLES = [
  ["rival_all", "All rival notifications"],
  ["overtake", "Overtake notifications"],
  ["rival_achievement", "Rival achievements"],
  ["daily", "Daily Expedition reminders"],
  ["rank_title", "Rank & title notifications"],
  ["crew_recovery", "Crew & recovery notifications"],
  ["season", "Season notifications"],
];

export default function Settings() {
  const [privacy, setPrivacy] = useState(null);
  const [prefs, setPrefs] = useState({});
  const [alias, setAlias] = useState("");

  const load = () => {
    api.get("/settings/privacy").then((r) => setPrivacy(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/notifications/preferences").then((r) => setPrefs(r.data || {})).catch(() => {});
    api.get("/settings/alias").then((r) => setAlias(r.data.alias)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const togglePrivacy = async (key) => {
    const val = !privacy[key];
    setPrivacy((p) => ({ ...p, [key]: val }));
    try { await api.put("/settings/privacy", { [key]: val }); toast.success("Saved"); }
    catch (e) { toast.error(apiErr(e)); load(); }
  };
  const togglePref = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try { await api.put("/notifications/preferences", { disabled: next }); toast.success("Saved"); }
    catch (e) { toast.error(apiErr(e)); load(); }
  };
  const regenAlias = async () => {
    try { const { data } = await api.post("/settings/alias/regenerate"); setAlias(data.alias); toast.success("New alias assigned"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!privacy) return <div className="py-20 text-center text-ff-muted">Loading settings…</div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center gap-2">
        <GearSix size={26} weight="duotone" className="text-ff-gold" />
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Settings</h1>
      </div>

      <section className="ff-panel rounded-md p-5" data-testid="settings-privacy">
        <h2 className="mb-1 flex items-center gap-2 font-display text-xl font-semibold"><ShieldCheck size={20} weight="duotone" className="text-ff-brass" /> Privacy & Rivals</h2>
        <p className="mb-3 text-xs text-ff-muted">You can stay ranked anonymously while preventing direct rival following. Changes apply immediately.</p>
        <div className="space-y-1.5">
          {PRIVACY_TOGGLES.map(([key, label]) => (
            <label key={key} className="flex cursor-pointer items-center justify-between rounded-sm px-2 py-2 hover:bg-ff-bg/50" data-testid={`privacy-${key}`}>
              <span className="text-sm text-ff-parchment">{label}</span>
              <input type="checkbox" checked={!!privacy[key]} onChange={() => togglePrivacy(key)} className="h-4 w-8 cursor-pointer accent-ff-gold" />
            </label>
          ))}
        </div>
        {privacy.anonymous_name && (
          <div className="mt-3 flex items-center justify-between rounded-sm border border-ff-brass/20 bg-ff-bg/50 p-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-ff-brass">Your anonymous alias</div>
              <div className="font-display text-lg text-ff-gold" data-testid="settings-alias">{alias}</div>
            </div>
            <button onClick={regenAlias} data-testid="alias-regen-btn" className="flex items-center gap-1 rounded-sm border border-ff-brass/30 px-3 py-1.5 text-xs hover:text-ff-gold"><ArrowsClockwise size={14} /> Regenerate</button>
          </div>
        )}
      </section>

      <section className="ff-panel rounded-md p-5" data-testid="settings-notifications">
        <h2 className="mb-1 flex items-center gap-2 font-display text-xl font-semibold"><BellSlash size={20} weight="duotone" className="text-ff-brass" /> Notification Preferences</h2>
        <p className="mb-3 text-xs text-ff-muted">Toggle a category ON to <span className="text-ff-parchment">mute</span> it. System notices can't be disabled.</p>
        <div className="space-y-1.5">
          {NOTIF_TOGGLES.map(([key, label]) => (
            <label key={key} className="flex cursor-pointer items-center justify-between rounded-sm px-2 py-2 hover:bg-ff-bg/50" data-testid={`pref-${key}`}>
              <span className="text-sm text-ff-parchment">Mute: {label}</span>
              <input type="checkbox" checked={!!prefs[key]} onChange={() => togglePref(key)} className="h-4 w-8 cursor-pointer accent-ff-gold" />
            </label>
          ))}
        </div>
      </section>

      <AudioSettingsPanel />

      <BetaSection />

      <section className="ff-panel rounded-md p-5" data-testid="settings-install-app">
        <h2 className="mb-1 flex items-center gap-2 font-display text-xl font-semibold"><Compass size={20} weight="duotone" className="text-ff-brass" /> Install Aurumward</h2>
        <p className="mb-3 text-xs text-ff-muted">Add Aurumward to your home screen for a full-screen, app-like experience. It's a web app — nothing is downloaded to your device.</p>
        <div className="flex items-center gap-4">
          <img src={brandIconSquare} alt="Aurumward app icon" className="h-20 w-20 rounded-xl border border-ff-brass/30 object-cover" data-testid="appicon-preview" />
          <div className="flex-1"><InstallApp compact /></div>
        </div>
      </section>

      <DangerZone />
    </div>
  );
}

function BetaSection() {
  const { onboarding, updateOnboarding, openFeedback } = useBeta();
  const [busy, setBusy] = useState(false);
  const replay = async () => {
    setBusy(true);
    try {
      await updateOnboarding({ replay: true });
      toast.success("Walkthrough restarted");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <section className="ff-panel rounded-md p-5" data-testid="settings-beta">
      <h2 className="mb-1 flex items-center gap-2 font-display text-xl font-semibold"><Path size={20} weight="duotone" className="text-ff-brass" /> Beta & Help</h2>
      <p className="mb-3 text-xs text-ff-muted">Replay the getting-started walkthrough anytime, or tell the team what's working and what isn't.</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={replay} disabled={busy} data-testid="replay-walkthrough-btn"
          className="flex items-center gap-1.5 rounded-sm border border-ff-brass/30 px-4 py-2 text-sm text-ff-parchment transition-colors hover:border-ff-gold hover:text-ff-gold disabled:opacity-60">
          <ArrowsClockwise size={15} /> Replay walkthrough
        </button>
        <button onClick={openFeedback} data-testid="settings-feedback-btn"
          className="flex items-center gap-1.5 rounded-sm bg-ff-gold px-4 py-2 text-sm font-semibold text-ff-bg transition-all hover:bg-ff-brass active:scale-95">
          <ChatCircleDots size={15} weight="fill" /> Send feedback
        </button>
      </div>
      {onboarding?.replay_count > 0 && (
        <p className="mt-2 text-[11px] text-ff-muted" data-testid="replay-count">Replayed {onboarding.replay_count} time(s).</p>
      )}
    </section>
  );
}

function DangerZone() {
  const [status, setStatus] = useState(null);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/account/deletion-status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const pending = status?.status === "pending_deletion";
  const request = async () => {
    if (!pw) return toast.error("Enter your password to confirm");
    setBusy(true);
    try { await api.post("/account/delete-request", { password: pw }); toast.success("Deletion scheduled. You have 30 days to cancel."); setPw(""); setConfirm(false); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!pw) return toast.error("Enter your password to confirm");
    setBusy(true);
    try { await api.post("/account/delete-cancel", { password: pw }); toast.success("Deletion cancelled. Welcome back!"); setPw(""); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <section className="ff-panel rounded-md border border-red-500/30 p-5" data-testid="settings-danger-zone">
      <h2 className="mb-1 flex items-center gap-2 font-display text-xl font-semibold text-red-300">
        <Warning size={20} weight="duotone" /> Danger Zone
      </h2>
      {pending ? (
        <div data-testid="deletion-pending">
          <p className="mb-3 text-sm text-amber-200">Your account is scheduled for permanent deletion on <b>{(status.scheduled_purge_at || "").slice(0, 10)}</b>. Until then you can't play, but you can cancel below.</p>
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Confirm password"
            className="mb-2 w-full rounded-sm border border-ff-brass/30 bg-ff-bg p-2 text-sm" data-testid="deletion-password" />
          <button onClick={cancel} disabled={busy} className="rounded-sm bg-ff-gold px-4 py-2 text-sm font-semibold text-ff-bg disabled:opacity-50" data-testid="deletion-cancel-btn">
            Cancel deletion
          </button>
        </div>
      ) : (
        <div data-testid="deletion-active">
          <p className="mb-3 text-xs text-ff-muted">Delete your account and all player data. This starts a 30-day grace period — your account is deactivated immediately and removed from leaderboards, rivals, and public profiles. You can cancel within 30 days; after that it's permanently purged. See our <a href="/privacy" className="text-ff-brass underline">Privacy Policy</a>.</p>
          {!confirm ? (
            <button onClick={() => setConfirm(true)} className="rounded-sm border border-red-500/50 px-4 py-2 text-sm font-semibold text-red-300 hover:bg-red-500/10" data-testid="deletion-start-btn">
              Delete my account
            </button>
          ) : (
            <div>
              <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Confirm password"
                className="mb-2 w-full rounded-sm border border-red-500/40 bg-ff-bg p-2 text-sm" data-testid="deletion-password" />
              <div className="flex gap-2">
                <button onClick={request} disabled={busy} className="rounded-sm bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="deletion-confirm-btn">
                  Permanently schedule deletion
                </button>
                <button onClick={() => { setConfirm(false); setPw(""); }} className="rounded-sm border border-ff-brass/30 px-4 py-2 text-sm text-ff-muted" data-testid="deletion-abort-btn">Keep my account</button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
